/**
 * Rank Progress page — shows all 17 levels, highlights current,
 * shows promotion requirements and current user progress.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { USER_LEVELS } from '@uo/shared';
import { useAuthStore } from '../store/auth';
import { TopBar } from '../components/ui/TopBar';
import { IconButton } from '../components/ui/IconButton';
import { Card } from '../components/ui/Card';

const TIER_META: Record<string, { label: string; colour: string; bg: string }> = {
  E: { label: 'E 级（地球）', colour: 'text-text-secondary', bg: 'bg-surface-elevated' },
  A: { label: 'A 级（行星）', colour: 'text-token-ep1', bg: 'bg-token-ep1/10' },
  S: { label: 'S 级（宇宙）', colour: 'text-brand-300', bg: 'bg-brand-500/10' },
};

export function RankProgress() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const profile = useAuthStore((s) => s.profile);
  const currentCode = profile?.level_code ?? 'E1';

  const sorted = [...USER_LEVELS].sort((a, b) => a.sort - b.sort);
  const tiers = ['E', 'A', 'S'] as const;

  return (
    <div className="safe-top min-h-screen bg-surface-base pb-16">
      <div className="absolute inset-0 -z-10 bg-space opacity-70" />
      <TopBar
        left={
          <IconButton aria-label={t('common.back', '返回')} onClick={() => navigate(-1)}>
            <BackIcon />
          </IconButton>
        }
        title={t('common.levelSystem', '等级体系')}
      />

      {/* Current level banner */}
      <div className="mx-4 mt-4">
        <Card className="p-4">
        <div className="text-xs text-text-secondary">{t('common.currentLevel', '当前等级')}</div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-text-primary">{currentCode}</span>
          <span className="text-sm text-text-secondary">
            {USER_LEVELS.find((l) => l.code === currentCode)?.name ?? ''}
          </span>
        </div>
        {/* overall bar */}
        {(() => {
          const idx = sorted.findIndex((l) => l.code === currentCode);
          const pct = Math.round(((idx + 1) / sorted.length) * 100);
          return (
            <div className="mt-3">
              <div className="mb-1 flex justify-between text-xs text-text-secondary">
                <span>{t('common.overallProgress', '总体进度')}</span>
                <span>{idx + 1}/{sorted.length}</span>
              </div>
              <div className="h-2 rounded-full bg-surface-overlay">
                <div
                  className="h-full rounded-full bg-brand-400 transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })()}
        </Card>
      </div>

      {/* Tier sections */}
      {tiers.map((tier) => {
        const meta = TIER_META[tier]!;
        const levels = sorted.filter((l) => l.tier === tier);
        return (
          <div key={tier} className="mx-4 mt-4">
            <div className={`rounded-t-xl px-4 py-2 text-xs font-bold ${meta.bg} ${meta.colour}`}>
              {meta.label}
            </div>
            <div className="divide-y divide-surface-border rounded-b-xl bg-surface-card/60 border border-surface-border border-t-0">
              {levels.map((level) => {
                const isCurrent = level.code === currentCode;
                return (
                  <div
                    key={level.code}
                    className={`px-4 py-3 ${isCurrent ? 'bg-surface-elevated' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {isCurrent && (
                          <span className="inline-block h-2 w-2 rounded-full bg-brand-400" />
                        )}
                        <span className={`font-semibold ${isCurrent ? 'text-brand-300' : 'text-text-primary'}`}>
                          {level.code}
                        </span>
                        <span className="text-sm text-text-secondary">{level.name}</span>
                      </div>
                      {isCurrent && (
                        <span className="rounded-full bg-brand-500 px-2 py-0.5 text-xs font-bold text-white">
                          {t('common.current', '当前')}
                        </span>
                      )}
                    </div>

                    {/* Requirements */}
                    <div className="mt-2 space-y-1 text-xs text-text-secondary">
                      {Number(level.entry_fee) > 0 && (
                        <RequirementRow icon="💰" label={`${t('common.activationFee', '激活费')} ${level.entry_fee} ORB`} />
                      )}
                      {Number(level.direct_e2) > 0 && (
                        <RequirementRow icon="👥" label={`${t('common.directReferralE2', '直接推荐 E2+ 人数')} ≥ ${level.direct_e2}`} />
                      )}
                      {Number(level.small_market_perf) > 0 && (
                        <RequirementRow icon="📈" label={`${t('common.smallMarketPerf', '小市场业绩')} ≥ ${level.small_market_perf} ORB`} />
                      )}
                      {Number(level.donation_uo) > 0 && (
                        <RequirementRow icon="❤️" label={`${t('common.donationUo', '公益捐赠')} ≥ ${level.donation_uo} ORB`} />
                      )}
                      {Number(level.a5_in_small_market) > 0 && (
                        <RequirementRow icon="🌟" label={`${t('common.a5InSmallMarket', '小市场含 A5')} ≥ ${level.a5_in_small_market} ${t('common.people', '人')}`} />
                      )}
                      {Number(level.promotion_rp) > 0 && (
                        <RequirementRow icon="🎖️" label={`${t('common.promotionRp', '晋升奖')} ${level.promotion_rp} RP`} />
                      )}
                      {level.mentor_circles.length > 0 && (
                        <RequirementRow icon="🔗" label={`${t('common.mentorCircles', '辅导圈层')} ${level.mentor_circles.length} ${t('common.layer', '层')}`} />
                      )}
                      {level.weekly_ep_cap && Number(level.weekly_ep_cap) > 0 && (
                        <RequirementRow icon="📊" label={`${t('common.weeklyEpCap', '每周EP上限')} ${level.weekly_ep_cap}`} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="h-4" />
    </div>
  );
}

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5M12 5l-7 7 7 7" />
    </svg>
  );
}

function RequirementRow({ icon, label }: { icon: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span>{icon}</span>
      <span>{label}</span>
    </div>
  );
}
